import { Box, Button, Typography, TypographyProps } from "@mui/material";
import Modal from "../../Components/Modal/Modal";
import { useMessageInputState } from "../../State/Hooks/MessageDialog/useMessageInput";
import { useMessageScheduleState } from "../../State/Hooks/MessageDialog/useMessageSchedule";
import { MessageChannel, useQuotaMessaging } from "../../State/Hooks/useQuotaMessaging";

export interface DisplayQuotaUsageProps {
  channel: MessageChannel;
  recipientCount?: number;
  isScheduled?: boolean;
  sx?: TypographyProps["sx"];
}

export interface QuotaUsageConfirmationTextOrModalProps {
  channel: MessageChannel;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isGroup: boolean;
  selectedCandidatesCount: number;
  isScheduled: boolean;
  disableAction: boolean;
  confirmationAction: () => void;
}

export function DisplayQuotaUsage({
  channel,
  recipientCount = 1,
  isScheduled = false,
  sx,
}: DisplayQuotaUsageProps) {
  const { getQuotaStatus } = useQuotaMessaging();
  const { textSplitCount } = useMessageInputState();
  const { msgListQueue } = useMessageScheduleState();
  const segmentCount = isScheduled
    ? msgListQueue.reduce((acc, msg) => acc + (msg.segments ?? 1), 0)
    : (textSplitCount ?? 1);
  const quotaStatus = getQuotaStatus(channel, segmentCount, recipientCount);

  return (
    <Typography
      variant="caption"
      data-test="quota-message-text"
      color={quotaStatus.status}
      sx={{ maxWidth: "120ch", pt: 0.5, ...sx }}
    >
      {quotaStatus.message}
    </Typography>
  );
}

export function QuotaUsageConfirmationTextOrModal({
  channel,
  isOpen,
  setIsOpen,
  isGroup,
  selectedCandidatesCount,
  isScheduled,
  disableAction,
  confirmationAction,
}: QuotaUsageConfirmationTextOrModalProps) {
  const buttonText = isScheduled ? "Schedule Messages" : "Send Now";
  return (
    <>
      <Modal
        title="Review Message Quota Before Sending"
        dataTestId="quota-message-modal"
        content={
          <DisplayQuotaUsage
            channel={channel}
            recipientCount={selectedCandidatesCount}
            isScheduled={isScheduled}
          />
        }
        isOpen={isOpen}
        handleCloseModal={() => {
          setIsOpen(false);
        }}
        footerContent={
          <Button
            data-test="group-send-now"
            variant="contained"
            color="primary"
            disabled={disableAction}
            onClick={disableAction ? undefined : confirmationAction}
            sx={{ minHeight: "40px", margin: 0, px: 4 }}
          >
            {buttonText}
          </Button>
        }
      />
      {!isGroup && (
        <Box>
          <DisplayQuotaUsage
            channel={channel}
            recipientCount={selectedCandidatesCount}
            isScheduled={isScheduled}
            sx={{ maxWidth: "100%", display: "block", textAlign: "right", my: 1 }}
          />
        </Box>
      )}
    </>
  );
}
